import React, { useState } from "react";
import ReactECharts from "echarts-for-react";
import KpiCard from "../components/KpiCard";
import ChartCard from "../components/ChartCard";
import FilterPanel from "../components/FilterPanel";
import { useVistaQuery, useVistaTrend } from "../hooks/useQuery";
import { fmt, COLORS, shortSvc } from "../api";

export default function Overview() {
  const { data: summary } = useVistaQuery<any>("/api/query/summary");
  const { data: trend } = useVistaTrend(6);
  const { data: services } = useVistaQuery<any[]>("/api/query/by-service", { limit: 15 });
  const { data: accounts } = useVistaQuery<any[]>("/api/query/by-account");
  const { data: daily } = useVistaQuery<any[]>("/api/query/daily");
  const { data: regions } = useVistaQuery<any[]>("/api/query/by-region");
  const [svcChart, setSvcChart] = useState<"bar" | "pie" | "treemap">("pie");

  const usage = parseFloat(summary?.usage_cost) || 0;
  const sp = parseFloat(summary?.sp_discount) || 0;
  const bundled = parseFloat(summary?.bundled_discount) || 0;
  const net = usage + sp + bundled;
  const trendData = (trend as any[]) ?? [];
  const svcData = services ?? [];
  const acctData = accounts ?? [];
  const dailyData = daily ?? [];
  const regionData = regions ?? [];

  const svcNames = svcData.map(r => shortSvc(r.service));
  const svcValues = svcData.map(r => parseFloat(r.cost) || 0);

  return (
    <div className="animate-fadeIn space-y-4">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Overview</h1>
      </div>

      {/* Filter Panel */}
      <FilterPanel />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total Usage" value={fmt(usage)} />
        <KpiCard label="SP Discount" value={fmt(sp)} green />
        <KpiCard label="Bundled Discount" value={fmt(bundled)} green />
        <KpiCard label="Net Cost" value={fmt(net)} highlight />
      </div>

      {/* Monthly Trend */}
      <ChartCard title="Monthly Cost Trend" wide>
        <ReactECharts style={{ height: 280 }} option={{
          backgroundColor: "transparent", tooltip: { trigger: "axis" },
          grid: { left: 80, right: 30, top: 20, bottom: 40 },
          xAxis: { type: "category", data: trendData.map(r => r.month), axisLabel: { color: "#9aa0b0" } },
          yAxis: { type: "value", axisLabel: { color: "#9aa0b0" }, splitLine: { lineStyle: { color: "#2a2d3e" } } },
          series: [{ type: "line", data: trendData.map(r => parseFloat(r.cost) || 0), smooth: true,
            lineStyle: { width: 3, color: "#3b82f6" },
            areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: "rgba(59,130,246,0.3)" }, { offset: 1, color: "rgba(59,130,246,0.02)" }] } },
            itemStyle: { color: "#3b82f6" }, symbol: "circle", symbolSize: 6 }],
        }} />
      </ChartCard>

      {/* Daily Spend */}
      {dailyData.length > 0 && (
        <ChartCard title="Daily Spend" wide>
          <ReactECharts style={{ height: 250 }} option={{
            backgroundColor: "transparent", tooltip: { trigger: "axis" },
            grid: { left: 80, right: 30, top: 20, bottom: 50 },
            xAxis: { type: "category", data: dailyData.map(r => r.day), axisLabel: { color: "#9aa0b0", rotate: 45, fontSize: 10 } },
            yAxis: { type: "value", axisLabel: { color: "#9aa0b0" }, splitLine: { lineStyle: { color: "#2a2d3e" } } },
            series: [{ type: "bar", data: dailyData.map(r => parseFloat(r.cost) || 0), barWidth: "60%",
              itemStyle: { borderRadius: [4, 4, 0, 0], color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: "#3b82f6" }, { offset: 1, color: "#8b5cf6" }] } } }],
          }} />
        </ChartCard>
      )}

      {/* Service + Account side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Cost by Service" actions={
          <div className="flex gap-1">
            {(["pie", "bar", "treemap"] as const).map(t => (
              <button key={t} onClick={() => setSvcChart(t)}
                className={`px-2 py-0.5 rounded text-[10px] border transition-all ${svcChart === t ? "bg-blue-500 text-white border-blue-500" : "border-[#2a2d3e] text-gray-400"}`}>
                {t}
              </button>
            ))}
          </div>
        }>
          <ReactECharts key={svcChart} style={{ height: 350 }} option={
            svcChart === "pie" ? {
              backgroundColor: "transparent", tooltip: { trigger: "item" },
              series: [{ type: "pie", radius: ["40%", "70%"],
                data: svcData.slice(0, 10).map((r, i) => ({ name: shortSvc(r.service), value: parseFloat(r.cost) || 0, itemStyle: { color: COLORS[i % COLORS.length] } })),
                label: { color: "#e8eaed", fontSize: 10, formatter: "{b}: {d}%" },
                labelLine: { show: true, lineStyle: { color: "#4a4d5e" } },
                emphasis: { itemStyle: { shadowBlur: 20 } } }],
            } : svcChart === "treemap" ? {
              backgroundColor: "transparent", tooltip: {},
              series: [{ type: "treemap",
                data: svcNames.slice(0, 12).map((n, i) => ({ name: n, value: svcValues[i], itemStyle: { color: COLORS[i % COLORS.length], borderColor: "#1e2130", borderWidth: 2 } })),
                label: { show: true, formatter: "{b}\n{c}", color: "#fff", fontSize: 11 }, breadcrumb: { show: false } }],
            } : {
              backgroundColor: "transparent", tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
              grid: { left: 140, right: 50, top: 10, bottom: 10 },
              xAxis: { type: "value", axisLabel: { color: "#9aa0b0" }, splitLine: { lineStyle: { color: "#2a2d3e" } } },
              yAxis: { type: "category", data: svcNames.slice(0, 10).reverse(), axisLabel: { color: "#e8eaed", fontSize: 10 } },
              series: [{ type: "bar", data: svcValues.slice(0, 10).reverse().map((v, i) => ({ value: v, itemStyle: { color: COLORS[i % COLORS.length] } })),
                barWidth: "55%", itemStyle: { borderRadius: [0, 4, 4, 0] },
                label: { show: true, position: "right", color: "#9aa0b0", fontSize: 10 } }],
            }
          } />
        </ChartCard>

        <ChartCard title="Cost by Account">
          <ReactECharts style={{ height: 350 }} option={{
            backgroundColor: "transparent", tooltip: { trigger: "axis" },
            grid: { left: 80, right: 30, top: 30, bottom: 120 },
            xAxis: { type: "category", data: acctData.map(r => r.account_name || r.account_id), axisLabel: { color: "#9aa0b0", rotate: 45, fontSize: 9, interval: 0 } },
            yAxis: { type: "value", name: "Cost (USD)", axisLabel: { color: "#9aa0b0" }, splitLine: { lineStyle: { color: "#2a2d3e" } } },
            series: [{ type: "bar", data: acctData.map((r, i) => ({ value: parseFloat(r.cost) || 0, itemStyle: { color: COLORS[i % COLORS.length] } })),
              barWidth: "50%", itemStyle: { borderRadius: [4, 4, 0, 0] } }],
          }} />
        </ChartCard>
      </div>

      {/* Regions */}
      {regionData.length > 0 && (
        <ChartCard title="Cost by Region" wide>
          <ReactECharts style={{ height: Math.max(300, Math.min(regionData.length, 10) * 35 + 60) }} option={{
            backgroundColor: "transparent", tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
            grid: { left: 160, right: 80, top: 10, bottom: 20 },
            xAxis: { type: "value", axisLabel: { color: "#9aa0b0" }, splitLine: { lineStyle: { color: "#2a2d3e" } } },
            yAxis: { type: "category", data: regionData.slice(0, 10).map(r => r.region || "global").reverse(), axisLabel: { color: "#e8eaed", fontSize: 11 } },
            series: [{ type: "bar", data: regionData.slice(0, 10).reverse().map((r, i) => ({ value: parseFloat(r.cost) || 0, itemStyle: { color: COLORS[i % COLORS.length] } })),
              barWidth: "55%", itemStyle: { borderRadius: [0, 4, 4, 0] } }],
          }} />
        </ChartCard>
      )}
    </div>
  );
}
